'use client'

/**
 * Admin dashboard sign-in through the organisation's identity provider (identity integrations wave 2,
 * 27 Oct 2026), as the dashboard talks to the API.
 *
 * HumanFirewall is a security-awareness platform; this is the customer's own identity integration with the HF admin
 * dashboard. Two conversations:
 *   - sign-in (public): POST /auth/sso/initiate { email } -> the identity provider's URL; the provider comes back to
 *     the API, which sends the browser to /auth/sso/complete?handoff=<one-time code>; POST /auth/sso/exchange
 *     { code } answers exactly what a password login does (tokens, or the 2FA challenge);
 *   - refusals: every one carries an errorCode, worded here from the catalogue (identitySso.adminSignIn.errors.*),
 *     never the API's English.
 *
 * The company's policy (GET / PUT /identity/sso/admin-policy and its hooks) lived here until branded sign-in: it is
 * replaced by Settings > Security > Sign-in (sign_in_policies) and its routes are removed.
 *
 * Branded sign-in (3 Nov 2026; contract docs/superpowers/specs/2026-11-03-branded-sign-in-design.md sections 3 and 4.2):
 * the company's own apps start here with their method key (`org_microsoft` ...), on a company address with no e-mail
 * at all, and HumanFirewall's shared Microsoft and Google send the SHA-256 of the same tab binding, so they also come
 * back through /auth/sso/complete?handoff= instead of putting the session in the address bar (decision 8). Refusals of
 * the new rules are worded from the sign-in-errors catalogue (lib/sign-in/error-keys.ts); the older codes keep theirs.
 */

import { ApiClientError, fetchApi } from '@/lib/api/client'
import type { LoginResponse } from '@/lib/api/auth'
import { normalizeTimeZone } from '@/lib/time/zone'
import { SIGN_IN_ERROR_KEYS } from '@/lib/sign-in/error-keys'
import type { OrgMethodKey } from '@/lib/sign-in/methods'

// ── Sign-in ─────────────────────────────────────────────────────────────────

export interface AdminSsoInitiateResponse {
  /** The identity provider's sign-in page (SAML AuthnRequest or OIDC authorize). */
  redirectUrl: string
  protocol: 'saml' | 'oidc'
}

/** What the exchange answers: a session, or the 2FA challenge, exactly as POST /auth/login. */
export type AdminSsoExchangeResponse = LoginResponse & { mustSetup2FA?: boolean }

/** The browser's current IANA zone (as on the password login), or undefined. */
function deviceTimeZone(): string | undefined {
  try {
    return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone) ?? undefined
  } catch {
    return undefined
  }
}

/**
 * Where this tab keeps the browser binding between initiate and the exchange: a random value sent at both, so the
 * one-time code works only in the browser that started the sign-in (a code that reached another browser through a
 * link is worth nothing there). sessionStorage: per tab and per origin, and it survives the trip to the identity
 * provider and back.
 */
export const ADMIN_SSO_BINDING_KEY = 'hf-admin-sso-binding'

/** 32 random bytes, base64url (43 characters). */
export function newSsoBinding(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  let text = ''
  for (const b of bytes) text += String.fromCharCode(b)
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** A fresh binding for this tab, kept where /auth/sso/complete reads it back. */
export function startSsoBinding(): string {
  const binding = newSsoBinding()
  sessionStorage.setItem(ADMIN_SSO_BINDING_KEY, binding)
  return binding
}

/** Lower-case hex SHA-256 of a text (the shared apps carry the binding's hash; the binding never leaves the tab). */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Where HumanFirewall's shared Microsoft or Google sign-in starts. `redirectTo` is the origin this tab is on (the API
 * allowlists it: our product hosts, or a live address of the person's own company); `bindingHash` makes the API store
 * the session behind a one-time code for this tab and send the browser to /auth/sso/complete?handoff=.
 */
export function sharedAppStartUrl(provider: 'microsoft' | 'google', origin: string, bindingHash: string): string {
  const api = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4001'
  const query = new URLSearchParams({ redirectTo: origin, bindingHash })
  return `${api}/v1/auth/${provider}?${query.toString()}`
}

export const adminSsoApi = {
  /**
   * Starts a sign-in with the organisation's identity provider. `email` names the company on dash (the e-mail's
   * account, today's rule); on a company address the address names it and no e-mail is sent. `method` picks one of the
   * company's own apps when it has several.
   */
  initiate(email: string | null, binding: string, method?: OrgMethodKey): Promise<AdminSsoInitiateResponse> {
    const body: { email?: string; binding: string; method?: OrgMethodKey } = { binding }
    if (email) body.email = email
    if (method) body.method = method
    return fetchApi<AdminSsoInitiateResponse>('/auth/sso/initiate', {
      method: 'POST',
      body: JSON.stringify(body),
      skipAuth: true,
    })
  },

  exchange(code: string, binding: string): Promise<AdminSsoExchangeResponse> {
    return fetchApi<AdminSsoExchangeResponse>('/auth/sso/exchange', {
      method: 'POST',
      body: JSON.stringify({ code, binding, timezone: deviceTimeZone() }),
      skipAuth: true,
    })
  },
}

/** Leave for the identity provider (a full navigation; its own function so a test can watch it). */
export function goToIdentityProvider(url: string): void {
  window.location.assign(url)
}

/** Only an http(s) address is ever opened: the identity provider's page. */
export function isOpenableIdpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

const S = 'identitySso.adminSignIn.errors'

/** Every code an admin sign-in can be refused with, and the sentence for it. */
const SIGN_IN_KEYS: ReadonlyMap<string, string> = new Map([
  ['admin_sso_unavailable', `${S}.unavailable`],
  ['admin_sso_no_account', `${S}.noAccount`],
  ['admin_sso_expired', `${S}.expired`],
  ['sso_required', `${S}.ssoRequired`],
  ['portal_sso_expired', `${S}.attemptExpired`],
  ['portal_sso_failed', `${S}.failed`],
  ['portal_sso_not_configured', `${S}.notConfigured`],
  ['portal_sso_mfa_required', `${S}.mfaRequired`],
  ['portal_sso_email_missing', `${S}.emailMissing`],
  ['company_disabled', `${S}.companyDisabled`],
])

export const ADMIN_SIGN_IN_ERROR_CODES: readonly string[] = [...SIGN_IN_KEYS.keys()]

export const ADMIN_SIGN_IN_UNKNOWN_KEY = `${S}.unknown`

/** The sentence for a sign-in refusal code (`?sso_error=`, or an API errorCode); the generic one for any other. */
export function adminSignInErrorKey(code: string | null | undefined): string {
  return (code ? SIGN_IN_KEYS.get(code.trim()) : undefined) ?? ADMIN_SIGN_IN_UNKNOWN_KEY
}

/**
 * The sentence for ANY admin sign-in refusal: a branded sign-in code (sign_in_*, company_address_*) from the
 * sign-in-errors catalogue, every older code as before.
 */
export function adminRefusalKey(code: string | null | undefined): string {
  const known = code ? SIGN_IN_ERROR_KEYS[code.trim()] : undefined
  return known ?? adminSignInErrorKey(code)
}

/** The two codes that mean "an account of another organisation on this company's address" (A6). */
const WRONG_COMPANY_CODES: ReadonlySet<string> = new Set(['sign_in_wrong_company_address', 'company_address_wrong_company'])

export function isWrongCompanyCode(code: string | null | undefined): boolean {
  return Boolean(code) && WRONG_COMPANY_CODES.has(String(code).trim())
}

/** The errorCode of a thrown API refusal, or null. */
export function errorCodeOf(error: unknown): string | null {
  return error instanceof ApiClientError ? error.errorCode : null
}

/** The company requires single sign-on and this sign-in used another method. */
export function isSsoRequired(error: unknown): boolean {
  return errorCodeOf(error) === 'sso_required'
}

/**
 * What the sign-in page says for an sso_required refusal (release 7, 28 Oct 2026): when the API adds
 * `reason: 'sso_paused'` (no connection of the company can sign an admin in right now, for example after a certificate
 * rollover), that single sign-on is paused and the emergency owner can still sign in and approve it; else "use SSO".
 */
export function ssoRequiredNoticeKey(error: unknown): string {
  const body = error instanceof ApiClientError ? (error.body as { reason?: unknown } | null | undefined) : null
  return body && typeof body === 'object' && body.reason === 'sso_paused' ? `${S}.ssoPaused` : `${S}.ssoRequired`
}
